import { useEffect } from "react";

interface BreadcrumbItem {
  name: string;
  url: string;
}

interface PageSEOProps {
  title: string;
  description: string;
  canonical: string;
  breadcrumbs: BreadcrumbItem[];
  pageType?: "WebPage" | "AboutPage" | "ContactPage" | "FAQPage" | "CollectionPage";
  ogImage?: string;
}

const FAQ_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": [
    {
      "@type": "Question",
      "name": "¿Cuánto cuesta el vaciado de un piso en Girona?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "El precio depende del volumen, el estado del inmueble y el tipo de residuos. Ofrecemos presupuesto sin compromiso tras una visita o valoración por fotos. El servicio mínimo equivale a una furgoneta completa."
      }
    },
    {
      "@type": "Question",
      "name": "¿Cuánto tiempo tarda un vaciado de piso?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Un piso estándar suele vaciarse en un solo día. Los casos más complejos, como limpiezas por síndrome de Diógenes, pueden requerir 2-3 días según el estado."
      }
    },
    {
      "@type": "Question",
      "name": "¿Qué hacéis con los residuos y muebles retirados?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Gestionamos los residuos de forma responsable: lo que tiene valor se dona o recicla, y el resto se lleva a puntos de reciclaje autorizados cumpliendo la normativa vigente."
      }
    },
    {
      "@type": "Question",
      "name": "¿Ofrecéis servicio urgente?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Sí, disponemos de servicio urgente para casos que requieren actuación rápida. Contáctanos por teléfono o WhatsApp y valoramos tu situación."
      }
    },
    {
      "@type": "Question",
      "name": "¿El servicio es discreto? ¿Se enterarán los vecinos?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Trabajamos con total discreción. En casos sensibles como el síndrome de Diógenes, coordinamos los accesos para minimizar la visibilidad y respetar la intimidad."
      }
    },
    {
      "@type": "Question",
      "name": "¿Recogéis muebles sueltos?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "No ofrecemos recogida de muebles sueltos. El servicio mínimo es una furgoneta completa. Para vaciados parciales o completos, contáctanos para valorar tu caso."
      }
    },
    {
      "@type": "Question",
      "name": "¿Trabajáis fuera de Girona ciudad?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Sí, cubrimos toda la comarca del Gironès y poblaciones cercanas: Salt, Sarrià de Ter, Quart, Vilablareix, Fornells de la Selva, Bescanó, Celrà, Cassà de la Selva y Banyoles."
      }
    },
    {
      "@type": "Question",
      "name": "¿Puedo pedir presupuesto por fotos sin visita?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Sí, en la mayoría de casos podemos darte un presupuesto orientativo por fotos o vídeo. Para casos complejos recomendamos una visita gratuita sin compromiso."
      }
    }
  ]
};

/**
 * PageSEO: injects per-page meta tags, BreadcrumbList, WebPage schema and FAQPage schema.
 * Handles SPA navigation correctly by cleaning up on unmount.
 */
export default function PageSEO({
  title,
  description,
  canonical,
  breadcrumbs,
  pageType = "WebPage",
  ogImage,
}: PageSEOProps) {
  useEffect(() => {
    // --- Title ---
    document.title = title;

    // --- Meta description ---
    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) {
      metaDesc = document.createElement("meta");
      metaDesc.setAttribute("name", "description");
      document.head.appendChild(metaDesc);
    }
    metaDesc.setAttribute("content", description);

    // --- Canonical ---
    let linkCanonical = document.querySelector('link[rel="canonical"]');
    if (!linkCanonical) {
      linkCanonical = document.createElement("link");
      linkCanonical.setAttribute("rel", "canonical");
      document.head.appendChild(linkCanonical);
    }
    linkCanonical.setAttribute("href", canonical);

    // --- OG tags ---
    const ogTags: Record<string, string> = {
      "og:title": title,
      "og:description": description,
      "og:url": canonical,
    };
    if (ogImage) ogTags["og:image"] = ogImage;

    Object.entries(ogTags).forEach(([property, content]) => {
      let tag = document.querySelector(`meta[property="${property}"]`);
      if (!tag) {
        tag = document.createElement("meta");
        tag.setAttribute("property", property);
        document.head.appendChild(tag);
      }
      tag.setAttribute("content", content);
    });

    // --- BreadcrumbList JSON-LD ---
    const removeById = (id: string) => {
      const el = document.getElementById(id);
      if (el) el.remove();
    };

    removeById("breadcrumb-jsonld");
    const breadcrumbScript = document.createElement("script");
    breadcrumbScript.id = "breadcrumb-jsonld";
    breadcrumbScript.type = "application/ld+json";
    breadcrumbScript.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": breadcrumbs.map((item, index) => ({
        "@type": "ListItem",
        "position": index + 1,
        "name": item.name,
        "item": item.url,
      })),
    });
    document.head.appendChild(breadcrumbScript);

    // --- WebPage schema (typed per page) ---
    removeById("webpage-jsonld");
    const webpageScript = document.createElement("script");
    webpageScript.id = "webpage-jsonld";
    webpageScript.type = "application/ld+json";
    webpageScript.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": pageType,
      "name": title,
      "description": description,
      "url": canonical,
      "inLanguage": "es-ES",
      "isPartOf": {
        "@type": "WebSite",
        "name": "GIROBUIT",
        "url": "https://giornabuit.com"
      },
      "breadcrumb": {
        "@type": "BreadcrumbList",
        "itemListElement": breadcrumbs.map((item, index) => ({
          "@type": "ListItem",
          "position": index + 1,
          "name": item.name,
          "item": item.url,
        })),
      },
      "publisher": {
        "@type": "Organization",
        "name": "GIROBUIT",
        "url": "https://giornabuit.com",
        "logo": {
          "@type": "ImageObject",
          "url": "https://public.readdy.ai/ai/img_res/dd390f0ef03d8b86574cad703c5770ab.jpg"
        }
      }
    });
    document.head.appendChild(webpageScript);

    // --- FAQPage schema (only on /faq) ---
    removeById("faqpage-jsonld");
    if (pageType === "FAQPage") {
      const faqScript = document.createElement("script");
      faqScript.id = "faqpage-jsonld";
      faqScript.type = "application/ld+json";
      faqScript.textContent = JSON.stringify(FAQ_SCHEMA);
      document.head.appendChild(faqScript);
    }

    return () => {
      removeById("breadcrumb-jsonld");
      removeById("webpage-jsonld");
      removeById("faqpage-jsonld");
    };
  }, [title, description, canonical, breadcrumbs, pageType, ogImage]);

  return null;
}
